import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { api } from '../../lib/api'
import { useSyncJob } from '../../lib/useSyncJob'
import { ClientSelect } from '../../components/ClientSelect'
import { useClientSelection } from '../../stores/clientSelection'
import { ListFilters, EMPTY_FILTERS, type FilterState } from '../../components/ListFilters'
import { Pagination } from '../../components/Pagination'
import { fmtMoney, fmtDate } from '../../lib/format'

interface Row {
  id: number
  number: string
  emagOrderId: string | null
  date: string
  customerName: string | null
  totalWithVat: string
  isStorno: boolean
  country: string
  currency: string
}

const TARI = ['RO', 'BG', 'HU'] as const
const STEAG: Record<string, string> = { RO: '🇷🇴', BG: '🇧🇬', HU: '🇭🇺' }
interface ListResp {
  items: Row[]
  total: number
  page: number
  pageSize: number
}

export function FacturiVanzarePage() {
  const clientId = useClientSelection((s) => s.selectedClientId)
  const [country, setCountry] = useState<string>('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)

  const { data, isLoading } = useQuery({
    queryKey: ['emag-customer-invoices', clientId, page, pageSize, filters, country],
    queryFn: async () =>
      (await api.get<ListResp>('/emag/customer-invoices', { params: {
          clientId,
          ...(country ? { country } : {}),
          page,
          pageSize,
          ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
          ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
        } })).data,
    enabled: clientId != null,
  })

  const sync = useSyncJob('invoices', clientId, {
    successText: (r) => `Sincronizate ${r.customer ?? 0} facturi vânzare, ${r.supplier ?? 0} cumpărare`,
    invalidate: ['emag-customer-invoices', 'emag-supplier-invoices'],
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-b4l-text">Facturi vânzare</h1>
        <div className="flex items-center gap-2">
          <ClientSelect />
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1) }}
            className="rounded-md border border-b4l-border px-2.5 py-1.5 text-[13px] outline-none focus:border-b4l-green"
          >
            <option value="">Toate țările</option>
            {TARI.map((t) => (
              <option key={t} value={t}>{STEAG[t]} {t}</option>
            ))}
          </select>
          <button
            onClick={() => void sync.start()}
            disabled={clientId == null || sync.running}
            className="flex items-center gap-1.5 rounded-md bg-b4l-green px-3 py-1.5 text-[13px] font-medium text-white hover:bg-b4l-green-dark disabled:opacity-60"
          >
            <RefreshCw size={14} className={sync.running ? 'animate-spin' : ''} />
            {sync.running ? 'Sincronizare în curs…' : 'Sincronizează'}
          </button>
        </div>
      </div>

      <div className="mb-3">
        <ListFilters value={filters} onChange={(v) => { setFilters(v); setPage(1) }} searchPlaceholder="număr, comandă, client…" />
      </div>

      {/* fara overflow-hidden: ar anula pozitionarea lipicioasa a antetului */}
      <div className="rounded-xl border border-b4l-border bg-white">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="sticky top-0 z-10 border-b border-b4l-border bg-white text-left text-b4l-text-muted shadow-[0_1px_0_0_rgb(229,231,235)]">
              <th className="px-4 py-2.5 font-medium">Țara</th>
              <th className="px-4 py-2.5 font-medium">Număr</th>
              <th className="px-4 py-2.5 font-medium">Comandă</th>
              <th className="px-4 py-2.5 font-medium">Data</th>
              <th className="px-4 py-2.5 font-medium">Client</th>
              <th className="px-4 py-2.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {(isLoading || clientId == null) && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-b4l-text-dim">
                  {clientId == null ? 'Alege un client.' : 'Se încarcă…'}
                </td>
              </tr>
            )}
            {!isLoading && clientId != null && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-b4l-text-dim">
                  Nicio factură. Apasă „Sincronizează".
                </td>
              </tr>
            )}
            {items.map((f) => (
              <tr key={f.id} className="border-b border-b4l-border last:border-0">
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {STEAG[f.country] ?? ''} {f.country}
                </td>
                <td className="px-4 py-2.5 font-medium">
                  {f.number}
                  {f.isStorno && <span className="ml-1 text-b4l-red">(storno)</span>}
                </td>
                <td className="px-4 py-2.5">{f.emagOrderId ?? '—'}</td>
                <td className="px-4 py-2.5">{fmtDate(f.date)}</td>
                <td className="px-4 py-2.5">{f.customerName ?? '—'}</td>
                <td className="px-4 py-2.5 text-right">{fmtMoney(f.totalWithVat, f.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPage={setPage}
          onPageSize={setPageSize}
          unit="facturi"
        />
      </div>

    </div>
  )
}
